import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { getDefaultRedirectPath } from '../utils/redirectByRole';

function Login() {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const navigate = useNavigate();
  const { login, user } = useAuth();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!credentials.username || !credentials.password) {
      toast.error('Пожалуйста, заполните все поля');
      return;
    }

    try {
      setLoading(true);
      const result = await login(credentials.username, credentials.password);

      if (result.success) {
        toast.success('Добро пожаловать!');
        
        // Получаем данные пользователя для определения маршрута редиректа
        try {
          const userData = await api.getCurrentUser();
          const redirectPath = getDefaultRedirectPath(userData?.role);
          navigate(redirectPath);
        } catch (error) {
          // Если не удалось получить данные пользователя, редиректим на /users
          console.error('Failed to get user data:', error);
          navigate('/users');
        }
      } else {
        toast.error(result.error || 'Ошибка авторизации');
      }
    } catch (error) {
      toast.error('Произошла ошибка при входе');
      console.error('Login error:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();

    if (!forgotEmail) {
      toast.error('Введите email');
      return;
    }

    if (!validateEmail(forgotEmail)) {
      toast.error('Введите корректный email');
      return;
    }

    try {
      setForgotLoading(true);
      await api.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (error) {
      toast.error('Произошла ошибка. Попробуйте позже');
      console.error('Forgot password error:', error);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotSent(false);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Иван Везёт</h1>
          <p>Админ панель</p>
        </div>

        {!showForgotPassword ? (
          <>
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="username">Имя пользователя</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  placeholder="Введите имя пользователя"
                  value={credentials.username}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Пароль</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  placeholder="Введите пароль"
                  value={credentials.password}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary btn-large"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  'Войти'
                )}
              </button>
            </form>
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  textDecoration: 'underline'
                }}
              >
                Забыли пароль?
              </button>
            </div>
          </>
        ) : (
          <div className="login-form">
            {forgotSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  fontSize: '2.5rem',
                  marginBottom: '1rem'
                }}>✉️</div>
                <p style={{
                  color: '#16a34a',
                  fontSize: '1rem',
                  fontWeight: '500',
                  marginBottom: '1rem'
                }}>
                  Письмо отправлено
                </p>
                <p style={{
                  color: '#64748b',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem'
                }}>
                  Если указанный email зарегистрирован в системе, на него будет отправлено письмо с инструкциями по восстановлению пароля.
                </p>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="btn btn-primary btn-large"
                  style={{ width: '100%' }}
                >
                  Вернуться к входу
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <h3 style={{
                  textAlign: 'center',
                  marginBottom: '0.5rem',
                  color: '#1e293b',
                  fontSize: '1.125rem'
                }}>
                  Восстановление пароля
                </h3>
                <p style={{
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem'
                }}>
                  Введите email, привязанный к вашему аккаунту
                </p>
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label htmlFor="forgot-email">Email</label>
                  <input
                    id="forgot-email"
                    type="email"
                    required
                    placeholder="Введите email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    disabled={forgotLoading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="btn btn-primary btn-large"
                  style={{ width: '100%', marginBottom: '1rem' }}
                >
                  {forgotLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    'Отправить'
                  )}
                </button>
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      textDecoration: 'underline'
                    }}
                  >
                    Назад к входу
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;